/*
  Coles click-through tracking.

  ZIIP does not sell online: every purchase happens at Coles, so a click out
  to coles.com.au is the closest thing this site has to a conversion. One
  delegated listener catches every link to Coles — header, footer, product
  cards, the product buy box, the contact page, and any added later — rather
  than wiring each button, so a new Coles link is tracked without touching
  this file.

  Each click is published as the Shopify customer event `coles_click`. Shopify
  does not report custom events itself; a custom pixel (Settings › Customer
  events) subscribes to it and forwards it to the analytics tool. It is also
  pushed to window.dataLayer, so a Google Tag Manager container picks it up
  with no pixel at all.

  Payload:
    placement  where the link sits: the section type (header, footer,
               ziip-product, ziip-product-grid, ziip-contact …)
    product    the product handle, when the link belongs to one product (a
               card, or anything on a product page); empty otherwise
    link_url   the Coles URL — a product page, or the search results page
    link_text  the visible label, trimmed
    page_path  the page the click came from

  `click` and `auxclick` both count: middle-click and Cmd/Ctrl-click open
  Coles in a new tab without a plain click event. Keyboard Enter on a link
  fires `click`, so it is covered too.
*/
(() => {
  const COLES = /(^|\.)coles\.com\.au$/i;

  const isColesLink = (link) => {
    try {
      return COLES.test(new URL(link.href, location.href).hostname);
    } catch (error) {
      return false;
    }
  };

  // Shopify wraps every section in <div id="shopify-section-{id}" class="shopify-section ...">.
  // The id ends in the section's key ("…__header", "…__main"), which is stable and
  // readable; the template-scoped prefix in front of it is not, so it is dropped.
  const placementOf = (link) => {
    const section = link.closest('.shopify-section');
    if (!section) return 'unknown';
    const id = section.id.replace(/^shopify-section-/, '');
    return id.includes('__') ? id.split('__').pop() : id;
  };

  const productOf = (link) => {
    const owner = link.closest('[data-product-handle]');
    if (owner) return owner.dataset.productHandle;
    return document.documentElement.dataset.productHandle || '';
  };

  const track = (event) => {
    if (event.type === 'auxclick' && event.button !== 1) return;

    const link = event.target.closest && event.target.closest('a[href]');
    if (!link || !isColesLink(link)) return;

    const payload = {
      placement: placementOf(link),
      product: productOf(link),
      link_url: link.href,
      link_text: (link.textContent || '').replace(/\s+/g, ' ').trim(),
      page_path: location.pathname,
    };

    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('coles_click', payload);
      }
    } catch (error) {
      // Tracking must never stop the link from working.
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'coles_click', ...payload });
  };

  // Capture phase, so a handler further down that stops propagation cannot hide the click.
  document.addEventListener('click', track, true);
  document.addEventListener('auxclick', track, true);
})();
